import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Users, Building2, Loader2, Save, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { PageLoading } from "@/components/shared";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function SiteAccess() {
  const { t } = useTranslation();
  const [acc, setAcc] = useState(undefined);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/employer/module/TIME_TRACKING").then((r) => setAcc(r.data?.access || "none")).catch(() => setAcc("none"));
  }, []);
  useEffect(() => {
    if (acc !== "full" && acc !== "read_only") return;
    api.get("/time/sites").then((r) => { const s = r.data.sites || []; setSites(s); if (s[0]) setSiteId(s[0].id); }).catch(() => {});
  }, [acc]);
  useEffect(() => {
    if (!siteId) return;
    setData(null);
    api.get(`/time/sites/${siteId}/access`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [siteId]);

  if (acc === undefined) return <PageLoading />;
  if (acc === "none") return <Navigate to="/app/employer/time/upgrade" replace />;

  const toggleSub = (id) => setData({ ...data, subcontractors: data.subcontractors.map((s) => s.subcontractor_id === id ? { ...s, authorized: !s.authorized } : s) });

  const save = async () => {
    setBusy(true);
    try {
      const ids = data.subcontractors.filter((s) => s.authorized).map((s) => s.subcontractor_id);
      const r = await api.put(`/time/sites/${siteId}/access`, { internal_allowed: data.internal_allowed, subcontractor_ids: ids });
      setData(r.data);
      toast.success(t("sac.saved"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="site-access">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Link to="/app/employer/time" className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="sac-back"><ArrowLeft className="h-4 w-4" /> {t("sac.back")}</Link>
        <h1 className="ml-2 flex items-center gap-2 font-heading text-2xl font-extrabold text-navy"><KeyRound className="h-6 w-6 text-cyan-dark" /> {t("sac.title")}</h1>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-slate-500">{t("sac.subtitle")}</p>

      <div className="mb-4 max-w-md">
        <label className="mb-1 block text-xs font-medium text-slate-500">{t("sac.site")}</label>
        <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="sac-site">
          <option value="">{t("sac.selectSite")}</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {!siteId ? null : !data ? <PageLoading /> : (
        <div className="max-w-2xl space-y-6">
          {/* Internal */}
          <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="sac-internal">
            <div>
              <div className="flex items-center gap-2 font-semibold text-navy"><Users className="h-5 w-5 text-cyan-dark" /> {t("sac.internal")}</div>
              <p className="mt-1 text-sm text-slate-500">{t("sac.internalDesc")}</p>
              <p className="mt-1 text-xs text-slate-400">{data.internal_assigned} {t("hw.internalWorkers")}</p>
            </div>
            <Switch checked={data.internal_allowed} onCheckedChange={(v) => setData({ ...data, internal_allowed: v })} data-testid="sac-internal-toggle" />
          </div>

          {/* Subcontractors */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="sac-subs">
            <div className="mb-3 flex items-center gap-2 font-semibold text-navy"><Building2 className="h-5 w-5 text-cyan-dark" /> {t("sac.subsTitle")}</div>
            {data.subcontractors.length === 0 ? <p className="text-sm text-slate-400">{t("sac.noSubs")}</p> : (
              <div className="space-y-2">
                {data.subcontractors.map((s) => (
                  <label key={s.subcontractor_id} className="flex cursor-pointer items-center justify-between rounded-xl border border-slate-100 p-3 hover:bg-slate-50" data-testid={`sac-sub-${s.subcontractor_id}`}>
                    <div className="flex items-center gap-3">
                      <Checkbox checked={s.authorized} onCheckedChange={() => toggleSub(s.subcontractor_id)} data-testid={`sac-sub-cb-${s.subcontractor_id}`} />
                      <span className="text-sm font-medium text-navy">{s.name}</span>
                    </div>
                    <span className="text-xs text-slate-400">{s.eligible_workers} {t("sac.eligible")}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {acc === "full" && (
            <Button onClick={save} disabled={busy} className="rounded-full bg-navy text-white hover:bg-navy/90" data-testid="sac-save">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} {t("sac.save")}
            </Button>
          )}
          <p className="text-xs text-slate-400">{t("sac.note")}</p>
        </div>
      )}
    </div>
  );
}
